import { Box, Link, Typography, IconButton, Divider } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSun,
  faMoon,
  faEnvelope,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { useDarkMode } from "../contexts/ThemeContext";
import { useRole } from "../contexts/RoleContext";
import ChangeLanguageButton from "./ui/ChangeLanguageButton";
import { ROLE_COLORS } from "../constants/roles";

const Footer = () => {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { currentRole } = useRole();
  const currentColors = ROLE_COLORS[currentRole];

  return (
    <Box
      sx={{
        background: darkMode
          ? "linear-gradient(90deg, #1a1a1a 0%, #1f1f1f 100%)"
          : "linear-gradient(90deg, #2a2a2a 0%, #252525 100%)",
        borderTop: `2px solid ${darkMode ? "#3a3a3a" : "#444444"}`,
        padding: "10px 24px",
        minHeight: "64px",
        maxHeight: "64px",
        height: "64px",
        display: "flex",
        alignItems: "center",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Subtle accent line at top */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, transparent, ${currentColors.primary}, transparent)`,
          opacity: 0.4,
        }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: 2,
          position: "relative",
          zIndex: 1,
          width: "100%",
        }}
      >
        {/* Left Section - Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
            opacity: 0,
            animation: "fadeInUp 0.6s ease-out 0.1s forwards",
            "@keyframes fadeInUp": {
              from: {
                opacity: 0,
                transform: "translateY(10px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <ChangeLanguageButton darkMode={darkMode} />

          <Divider
            orientation="vertical"
            sx={{
              height: "28px",
              borderColor: darkMode ? "#3a3a3a" : "#444444",
              mx: 0.5,
            }}
          />

          <IconButton
            onClick={toggleDarkMode}
            size="small"
            sx={{
              padding: "8px",
              color: darkMode ? "#e0e0e0" : "#ffffff",
              borderRadius: "8px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              border: "1px solid transparent",
              "&:hover": {
                backgroundColor: darkMode ? "#2a2a2a" : "#3a3a3a",
                borderColor: currentColors.primary,
                color: currentColors.primary,
                transform: "translateY(-2px)",
              },
            }}
          >
            <FontAwesomeIcon
              icon={darkMode ? faSun : faMoon}
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        </Box>

        {/* Center Section - Contact Info */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            flexShrink: 1,
            minWidth: 0,
            opacity: 0,
            animation: "fadeInUp 0.6s ease-out 0.2s forwards",
          }}
        >
          <Link
            href="mailto:us@vrtta.green"
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: darkMode ? "#aaaaaa" : "#cccccc",
              fontFamily: "Poppins",
              fontSize: "0.875rem",
              fontWeight: 500,
              lineHeight: 1,
              transition: "all 0.3s ease",
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid transparent",
              whiteSpace: "nowrap",
              "&:hover": {
                color: currentColors.primary,
                backgroundColor: darkMode
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(255, 255, 255, 0.1)",
                borderColor: currentColors.primary,
                transform: "translateY(-2px)",
              },
            }}
          >
            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: "14px" }} />
            us@vrtta.green
          </Link>

          <Link
            href="tel:+14388825385"
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: darkMode ? "#aaaaaa" : "#cccccc",
              fontFamily: "Poppins",
              fontSize: "0.875rem",
              fontWeight: 500,
              lineHeight: 1,
              transition: "all 0.3s ease",
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid transparent",
              whiteSpace: "nowrap",
              "&:hover": {
                color: currentColors.primary,
                backgroundColor: darkMode
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(255, 255, 255, 0.1)",
                borderColor: currentColors.primary,
                transform: "translateY(-2px)",
              },
            }}
          >
            <FontAwesomeIcon icon={faPhone} style={{ fontSize: "14px" }} />
            +1 (438) 882-5385
          </Link>
        </Box>

        {/* Right Section - Copyright */}
        <Box
          sx={{
            textAlign: "right",
            lineHeight: 1.2,
            flexShrink: 0,
            opacity: 0,
            animation: "fadeInUp 0.6s ease-out 0.3s forwards",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "1rem",
              fontWeight: 600,
              color: darkMode ? "#888888" : "#999999",
              lineHeight: 1.2,
              marginBottom: "4px",
              letterSpacing: "0.5px",
              whiteSpace: "nowrap",
            }}
          >
            Punar © 2025
          </Typography>
          <Link
            href="https://vrtta.green"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              fontFamily: "Poppins",
              fontSize: "0.75rem",
              color: darkMode ? "#666666" : "#777777",
              lineHeight: 1.2,
              transition: "all 0.3s ease",
              whiteSpace: "nowrap",
              "&:hover": {
                color: currentColors.primary,
              },
            }}
          >
            VRTTA Green Solutions Inc.
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
